/**
 * DataFreshnessTag — Shows how fresh a piece of data is.
 *
 * Displays "Live · 2m ago" or "Stale · 3h ago" with a clock icon.
 * When stale, shows an amber warning color.
 * Optional [Refresh] button when data is stale.
 */
import React from 'react';
import { Clock, RefreshCw } from 'lucide-react';
import { clsx } from 'clsx';

interface DataFreshnessTagProps {
    /** ISO timestamp when data was fetched */
    fetchedAt: string | null;
    /** How long until data is considered stale (seconds). null = never stale */
    cacheMaxAgeSeconds?: number | null;
    /** Callback to refresh this specific section */
    onRefresh?: () => void;
    /** If true, currently refreshing */
    isRefreshing?: boolean;
    className?: string;
}

function formatAge(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

export const DataFreshnessTag: React.FC<DataFreshnessTagProps> = ({
    fetchedAt,
    cacheMaxAgeSeconds,
    onRefresh,
    isRefreshing = false,
    className,
}) => {
    if (!fetchedAt) return null;

    const age = Date.now() - new Date(fetchedAt).getTime();
    const isStale = cacheMaxAgeSeconds != null && age > cacheMaxAgeSeconds * 1000;

    return (
        <span className={clsx(
            "inline-flex items-center gap-1 text-[10px]",
            isStale ? "text-amber-500" : "text-muted-foreground/70",
            className
        )}>
            <Clock className="w-2.5 h-2.5" />
            <span>{isStale ? 'Stale' : 'Live'} · {formatAge(age)}</span>
            {isStale && onRefresh && (
                <button
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    className="ml-0.5 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                    title="Refresh this section"
                    type="button"
                >
                    <RefreshCw className={clsx("w-2.5 h-2.5", isRefreshing && "animate-spin")} />
                </button>
            )}
        </span>
    );
};
